// contexts/TimesheetContext.tsx
import React, { createContext, useContext, useState, useCallback } from "react";
import { useMutation, useLazyQuery } from "@apollo/client";
import { useAuth } from "./AuthContext";
import {
  GET_MY_TIMESHEET,
  CREATE_TIMESHEET,
  UPDATE_TIMESHEET_ENTRY,
  DELETE_TIMESHEET_ENTRY,
  SUBMIT_TIMESHEET,
  LIST_MY_TIMESHEETS,
  LIST_ALL_TIMESHEETS,
  APPROVE_TIMESHEET,
  REJECT_TIMESHEET,
} from "../graphql/timesheet";

interface TimesheetContextType {
  // State
  currentTimesheet: any;
  timesheets: any[];
  loading: boolean;

  // Actions
  loadCurrentTimesheet: (weekStart: string) => void;
  createTimesheet: (input: any) => Promise<any>;
  updateEntry: (
    timesheetId: string,
    entryId: string,
    updates: any
  ) => Promise<any>;
  deleteEntry: (timesheetId: string, entryId: string) => Promise<any>;
  submitTimesheet: (timesheetId: string) => Promise<any>;
  approveTimesheet: (timesheetId: string) => Promise<any>;
  rejectTimesheet: (timesheetId: string, reason?: string) => Promise<any>;
  loadMyTimesheets: () => void;
  loadAllTimesheets: (filters?: any) => void;

  // Utilities
  calculateHours: (
    startTime: string,
    endTime: string,
    breakMinutes: number
  ) => number;
  getCurrentWeek: () => { weekStart: string; weekEnd: string };
}

const TimesheetContext = createContext<TimesheetContextType | undefined>(
  undefined
);

export const useTimesheet = () => {
  const context = useContext(TimesheetContext);
  if (!context) {
    throw new Error("useTimesheet must be used within a TimesheetProvider");
  }
  return context;
};

export const TimesheetProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [currentTimesheet, setCurrentTimesheet] = useState<any>(null);
  const [timesheets, setTimesheets] = useState<any[]>([]);

  // GraphQL Operations
  const [loadTimesheet, { loading: timesheetLoading }] = useLazyQuery(
    GET_MY_TIMESHEET,
    {
      onCompleted: (data) => {
        setCurrentTimesheet(data.getMyTimesheet);
      },
      onError: (error) => {
        console.error("Error loading timesheet:", error);
        // If no timesheet exists, create an empty one locally
        const { weekStart, weekEnd } = getCurrentWeek();
        setCurrentTimesheet({
          id: `local-${Date.now()}`,
          employeeId: user?.username,
          weekStart,
          weekEnd,
          entries: [],
          totalHours: 0,
          status: "DRAFT",
        });
      },
      fetchPolicy: "network-only",
    }
  );

  const [createTimesheetMutation] = useMutation(CREATE_TIMESHEET, {
    onCompleted: (data) => {
      setCurrentTimesheet(data.createTimesheet);
    },
  });

  const [updateEntryMutation] = useMutation(UPDATE_TIMESHEET_ENTRY, {
    onCompleted: (data) => {
      setCurrentTimesheet(data.updateTimesheetEntry);
    },
  });

  const [deleteEntryMutation] = useMutation(DELETE_TIMESHEET_ENTRY, {
    onCompleted: (data) => {
      setCurrentTimesheet(data.deleteTimesheetEntry);
    },
  });

  const [submitTimesheetMutation] = useMutation(SUBMIT_TIMESHEET, {
    onCompleted: (data) => {
      setCurrentTimesheet((prev: any) => ({
        ...prev,
        status: data.submitTimesheet.status,
      }));
    },
  });

  const [approveTimesheetMutation] = useMutation(APPROVE_TIMESHEET);
  const [rejectTimesheetMutation] = useMutation(REJECT_TIMESHEET);

  const [loadMyTimesheetsQuery, { loading: myTimesheetsLoading }] =
    useLazyQuery(LIST_MY_TIMESHEETS, {
      onCompleted: (data) => {
        setTimesheets(data.listMyTimesheets.items);
      },
    });

  const [loadAllTimesheetsQuery, { loading: allTimesheetsLoading }] =
    useLazyQuery(LIST_ALL_TIMESHEETS, {
      onCompleted: (data) => {
        setTimesheets(data.listAllTimesheets.items);
      },
    });

  // Utility Functions
  const calculateHours = useCallback(
    (startTime: string, endTime: string, breakMinutes: number): number => {
      const start = new Date(`1970-01-01T${startTime}`);
      const end = new Date(`1970-01-01T${endTime}`);
      const diffMs = end.getTime() - start.getTime();
      const hours = diffMs / (1000 * 60 * 60) - breakMinutes / 60;
      return Math.max(0, parseFloat(hours.toFixed(2)));
    },
    []
  );

  const getCurrentWeek = useCallback(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    return {
      weekStart: startOfWeek.toISOString().split("T")[0],
      weekEnd: endOfWeek.toISOString().split("T")[0],
    };
  }, []);

  // Action Functions
  const loadCurrentTimesheet = useCallback(
    (weekStart: string) => {
      loadTimesheet({ variables: { weekStart } });
    },
    [loadTimesheet]
  );

  const createTimesheet = useCallback(
    async (input: any) => {
      try {
        const result = await createTimesheetMutation({ variables: { input } });
        return result.data.createTimesheet;
      } catch (error) {
        console.error("Error creating timesheet:", error);
        throw error;
      }
    },
    [createTimesheetMutation]
  );

  const updateEntry = useCallback(
    async (timesheetId: string, entryId: string, updates: any) => {
      try {
        const result = await updateEntryMutation({
          variables: {
            timesheetId,
            entryId,
            input: updates,
          },
        });
        return result.data.updateTimesheetEntry;
      } catch (error) {
        console.error("Error updating timesheet entry:", error);
        throw error;
      }
    },
    [updateEntryMutation]
  );

  const deleteEntry = useCallback(
    async (timesheetId: string, entryId: string) => {
      try {
        const result = await deleteEntryMutation({
          variables: {
            timesheetId,
            entryId,
          },
        });
        return result.data.deleteTimesheetEntry;
      } catch (error) {
        console.error("Error deleting timesheet entry:", error);
        throw error;
      }
    },
    [deleteEntryMutation]
  );

  const submitTimesheet = useCallback(
    async (timesheetId: string) => {
      try {
        const result = await submitTimesheetMutation({
          variables: { timesheetId },
        });
        return result.data.submitTimesheet;
      } catch (error) {
        console.error("Error submitting timesheet:", error);
        throw error;
      }
    },
    [submitTimesheetMutation]
  );

  const approveTimesheet = useCallback(
    async (timesheetId: string) => {
      try {
        const result = await approveTimesheetMutation({
          variables: { timesheetId },
        });
        return result.data.approveTimesheet;
      } catch (error) {
        console.error("Error approving timesheet:", error);
        throw error;
      }
    },
    [approveTimesheetMutation]
  );

  const rejectTimesheet = useCallback(
    async (timesheetId: string, reason?: string) => {
      try {
        const result = await rejectTimesheetMutation({
          variables: { timesheetId, reason },
        });
        return result.data.rejectTimesheet;
      } catch (error) {
        console.error("Error rejecting timesheet:", error);
        throw error;
      }
    },
    [rejectTimesheetMutation]
  );

  const loadMyTimesheets = useCallback(() => {
    loadMyTimesheetsQuery();
  }, [loadMyTimesheetsQuery]);

  const loadAllTimesheets = useCallback(
    (filters?: any) => {
      loadAllTimesheetsQuery({ variables: filters });
    },
    [loadAllTimesheetsQuery]
  );

  const loading =
    timesheetLoading || myTimesheetsLoading || allTimesheetsLoading;

  const value = {
    currentTimesheet,
    timesheets,
    loading,
    loadCurrentTimesheet,
    createTimesheet,
    updateEntry,
    deleteEntry,
    submitTimesheet,
    approveTimesheet,
    rejectTimesheet,
    loadMyTimesheets,
    loadAllTimesheets,
    calculateHours,
    getCurrentWeek,
  };

  return (
    <TimesheetContext.Provider value={value}>
      {children}
    </TimesheetContext.Provider>
  );
};
